import React, { useState, useEffect } from "react";
import config from "../../config/config";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    icon: "",
    image_url: "",
    link_url: "",
    is_active: true,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only start dragging after moving 8px
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("get_services.php"), {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();

      if (data.success) {
        setServices(data.services || []);
      } else {
        setMessage("Failed to fetch services: " + data.message);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      setMessage("Error fetching services");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      icon: "",
      image_url: "",
      link_url: "",
      is_active: true,
    });
    setEditingService(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setMessage("");

      const endpoint = editingService
        ? "update_service.php"
        : "create_service.php";

      const requestData = editingService
        ? { ...formData, id: editingService.id }
        : formData;

      const response = await fetch(config.getAdminApiUrl(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        setShowModal(false);
        resetForm();
        fetchServices(); // Refresh the list
      } else {
        setMessage("Failed to save service: " + data.message);
      }
    } catch (error) {
      console.error("Error saving service:", error);
      setMessage("Error saving service");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description || "",
      icon: service.icon || "",
      image_url: service.image_url || "",
      link_url: service.link_url || "",
      is_active: service.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) {
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const response = await fetch(config.getAdminApiUrl("delete_service.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: serviceId }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(data.message);
        fetchServices(); // Refresh the list
      } else {
        setMessage("Failed to delete service: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      setMessage("Error deleting service");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Handle drag and drop for services
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id && services && services.length > 0) {
      const oldIndex = services.findIndex(
        (service) => service.id === active.id
      );
      const newIndex = services.findIndex((service) => service.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newServices = arrayMove(services, oldIndex, newIndex);
        setServices(newServices);

        try {
          const response = await fetch(
            config.getAdminApiUrl("update_service_order.php"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-API-Key": "ncg_admin_2024",
              },
              body: JSON.stringify({ services: newServices }),
            }
          );

          const data = await response.json();
          if (!data.success) {
            alert("Error updating service order: " + data.message);
          }
        } catch (error) {
          console.error("Error updating service order:", error);
          alert("Error updating service order");
        }
      }
    }
  };

  // Sortable Service Row Component
  const SortableServiceRow = ({ service, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: service.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const handleButtonClick = (e) => {
      // Stop propagation to prevent drag from starting
      e.stopPropagation();
    };

    return (
      <tr
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`${!service.is_active ? "inactive-row" : ""} ${
          isDragging ? "dragging" : ""
        }`}
      >
        <td className="service-icon-cell">
          <div className="service-icon">
            <i className={service.icon || "fas fa-cog"}></i>
          </div>
        </td>
        <td className="service-title-cell">
          <strong>{service.title}</strong>
        </td>
        <td className="service-description-cell">
          <div className="description-text">{service.description}</div>
        </td>
        <td className="service-order-cell">
          <span className="order-badge">#{index + 1}</span>
        </td>
        <td className="service-status-cell">
          <span
            className={`status-badge ${
              service.is_active ? "active" : "inactive"
            }`}
          >
            {service.is_active ? "Active" : "Inactive"}
          </span>
        </td>
        <td className="service-link-cell">
          {service.link_url ? (
            <a
              href={service.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="link-button"
              title="View Link"
            >
              <i className="fas fa-external-link-alt"></i>
            </a>
          ) : (
            <span className="no-link">-</span>
          )}
        </td>
        <td className="service-actions-cell">
          <div className="action-buttons">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={(e) => {
                handleButtonClick(e);
                handleEdit(service);
              }}
              disabled={loading}
              title="Edit Service"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                handleButtonClick(e);
                handleDelete(service.id);
              }}
              disabled={loading}
              title="Delete Service"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="content-manager">
      <div className="content-header">
        <h2>Services Management</h2>
        <p>Manage the services displayed in the "Our Services" section</p>
      </div>

      {message && (
        <div
          className={`alert ${
            message.includes("successfully") ? "alert-success" : "alert-danger"
          }`}
        >
          {message}
        </div>
      )}

      <div className="content-actions">
        <button
          className="btn btn-primary"
          onClick={openAddModal}
          disabled={loading}
        >
          <i className="fas fa-plus me-2"></i>
          Add New Service
        </button>
      </div>

      {loading && (!services || services.length === 0) ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="services-table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={
                    services && services.length > 0
                      ? services.map((service) => service.id)
                      : []
                  }
                  strategy={verticalListSortingStrategy}
                >
                  {services && services.length > 0 ? (
                    services.map((service, index) => (
                      <SortableServiceRow
                        key={service.id}
                        service={service}
                        index={index}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="no-data">
                          <i className="fas fa-cogs"></i>
                          <p>
                            No services found. Click "Add New Service" to get
                            started.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {/* Add/Edit Service Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingService ? "Edit Service" : "Add New Service"}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Service Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Icon Class (FontAwesome)</label>
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="e.g., fas fa-industry"
                  />
                </div>

                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Link URL</label>
                  <input
                    type="url"
                    name="link_url"
                    value={formData.link_url}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Saving...
                    </>
                  ) : (
                    "Save Service"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .table-responsive {
          overflow-x: auto;
          margin-top: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .services-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          font-size: 0.9rem;
        }

        .services-table th {
          background: #f8f9fa;
          color: #495057;
          font-weight: 600;
          text-align: left;
          padding: 1rem;
          border-bottom: 2px solid #dee2e6;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .services-table td {
          padding: 1rem;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        .services-table tbody tr {
          transition: background-color 0.2s ease;
          cursor: grab;
        }

        .services-table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .services-table tbody tr:active {
          cursor: grabbing;
        }

        .services-table tbody tr.dragging {
          opacity: 0.8;
          transform: rotate(2deg);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .services-table tbody tr.inactive-row {
          opacity: 0.6;
          background-color: #f8f9fa;
        }

        .services-table tbody tr.inactive-row:hover {
          background-color: #e9ecef;
        }

        .service-icon-cell {
          width: 80px;
          text-align: center;
        }

        .service-icon {
          width: 40px;
          height: 40px;
          background: #f8f9ff;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #007bff;
          font-size: 1.2rem;
          margin: 0 auto;
        }

        .service-title-cell {
          font-weight: 600;
          color: #2c3e50;
          min-width: 150px;
        }

        .service-description-cell {
          max-width: 300px;
        }

        .description-text {
          color: #6c757d;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .service-order-cell {
          text-align: center;
          width: 80px;
        }

        .order-badge {
          background: #e9ecef;
          color: #495057;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .service-status-cell {
          text-align: center;
          width: 100px;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.active {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .service-link-cell {
          text-align: center;
          width: 80px;
        }

        .link-button {
          color: #007bff;
          text-decoration: none;
          padding: 0.5rem;
          border-radius: 4px;
          transition: all 0.2s ease;
          display: inline-block;
        }

        .link-button:hover {
          background: #f8f9ff;
          color: #0056b3;
          transform: scale(1.1);
        }

        .no-link {
          color: #6c757d;
          font-style: italic;
        }

        .service-actions-cell {
          text-align: center;
          width: 120px;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }

        .action-buttons .btn {
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          font-size: 0.8rem;
          transition: all 0.2s ease;
          cursor: pointer;
          position: relative;
          z-index: 10;
        }

        .action-buttons .btn:hover {
          transform: translateY(-1px);
        }

        .btn-outline-primary {
          border-color: #007bff;
          color: #007bff;
        }

        .btn-outline-primary:hover {
          background: #007bff;
          color: white;
        }

        .btn-outline-danger {
          border-color: #dc3545;
          color: #dc3545;
        }

        .btn-outline-danger:hover {
          background: #dc3545;
          color: white;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(2px);
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modal-header {
          padding: 1.5rem 1.5rem 1rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #2c3e50;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          padding: 1rem 1.5rem 1.5rem;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          background: #f8f9fa;
          border-radius: 0 0 16px 16px;
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6c757d;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .btn-close:hover {
          background: #e9ecef;
          color: #495057;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
          color: #495057;
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
          width: 18px;
          height: 18px;
          accent-color: #007bff;
        }

        .form-group {
          margin-bottom: 1.25rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #495057;
        }

        .form-control {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .form-control:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background: #0056b3;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #545b62;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </div>
  );
};

export default ServicesManager;
